import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Aviso, Botao, Campo, CampoDeMoeda, CampoDeSelecao, Modal } from '../../componentes/ui';
import { lerValorDigitado } from '../../servicos/formato';
import { requisicaoAutenticada } from '../../servicos/http';
import { ErroDaApi } from '../ErroDaApi';
import type { Negociacao, OpcaoDeModelo } from './tipos';

type Base = 'FACE' | 'VENDA';

/**
 * "Gerar termo" — o termo de comissionamento da negociação (D-020).
 *
 * Duas escolhas: o modelo (os da aba Contratos, ou o esqueleto padrão) e a
 * comissão, em R$ ou em %, sobre a face ou sobre o valor de venda — o cálculo
 * do outro número aparece enquanto se digita. As partes não se escolhem
 * (D-023): o fornecedor paga e o assessor da negociação recebe; sem assessor,
 * recebe a operadora.
 *
 * Gerar só guarda o PDF na negociação; mandar assinar é outro clique.
 */
export function GerarTermo(props: {
  readonly negociacao: Negociacao;
  readonly modelos: readonly OpcaoDeModelo[];
  readonly aoFechar: () => void;
  readonly aoGerar: (atualizada: Negociacao) => void;
}): JSX.Element {
  const n = props.negociacao;
  const [modeloId, setModeloId] = useState(props.modelos[0]?.id ?? '');
  const [emReais, setEmReais] = useState(false);
  const [valor, setValor] = useState('');
  const [percentual, setPercentual] = useState('');
  const [base, setBase] = useState<Base>('FACE');
  const [forma, setForma] = useState('');

  const valorDaBase = base === 'FACE' ? n.valorFace : n.valorVenda;
  const numero = emReais ? lerValorDigitado(valor) : lerValorDigitado(percentual);
  const calculado =
    numero === null || numero <= 0 || valorDaBase <= 0
      ? null
      : emReais
        ? `${((numero / valorDaBase) * 100).toFixed(2).replace('.', ',')}% ${base === 'FACE' ? 'da face' : 'do valor de venda'}`
        : `R$ ${reais((valorDaBase * numero) / 100)}`;

  const pronto = numero !== null && numero > 0;
  const comissionado = n.parceiroNome ?? (n.parceiroId === null ? 'A operadora (MS3)' : 'Assessor');

  const gerar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<Negociacao>(`/pontual/negociacoes/${n.id}/termo`, {
        metodo: 'POST',
        corpo: {
          modeloId: modeloId === '' ? null : modeloId,
          comissao: emReais ? { valor: numero, base } : { percentual: numero, base },
          ...(forma.trim() === '' ? {} : { formaDePagamento: forma.trim() }),
        },
      }),
    onSuccess: props.aoGerar,
  });

  return (
    <Modal
      aberto
      aoFechar={props.aoFechar}
      titulo="Gerar termo de comissionamento"
      descricao={`${n.fornecedorNome ?? 'Fornecedor'} → ${n.compradorNome ?? 'Comprador'}`}
      rodape={
        <>
          <Botao variante="secundario" onClick={props.aoFechar}>
            Cancelar
          </Botao>
          <Botao disabled={!pronto} carregando={gerar.isPending} onClick={() => gerar.mutate()}>
            Gerar termo
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <CampoDeSelecao
          rotulo="Modelo"
          ajuda="Os modelos de termo ficam em Configurações › Contratos."
          value={modeloId}
          onChange={(e) => setModeloId(e.target.value)}
          opcoes={[
            ...props.modelos.map((m) => ({ valor: m.id, rotulo: m.nome })),
            { valor: '', rotulo: 'Esqueleto padrão do Deca' },
          ]}
        />

        <dl className="grid gap-3 rounded-card border border-borda p-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs text-conteudo-sutil">Contratante · paga a comissão</dt>
            <dd className="text-conteudo-primario">{n.fornecedorNome ?? 'Fornecedor'}</dd>
          </div>
          <div>
            <dt className="text-xs text-conteudo-sutil">Comissionado · recebe</dt>
            <dd className="text-conteudo-primario">{comissionado}</dd>
          </div>
        </dl>

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-2 text-sm font-medium text-conteudo-primario">A comissão</legend>
          <Alternativas
            valor={emReais ? 'R$' : '%'}
            aoMudar={(v) => setEmReais(v === 'R$')}
            opcoes={[
              { valor: '%', rotulo: 'Em %' },
              { valor: 'R$', rotulo: 'Em R$' },
            ]}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            {emReais ? (
              <CampoDeMoeda rotulo="Valor da comissão" value={valor} onChange={setValor} />
            ) : (
              <Campo
                rotulo="Percentual da comissão"
                inputMode="decimal"
                placeholder="1,5"
                value={percentual}
                onChange={(e) => setPercentual(e.target.value)}
              />
            )}
            <CampoDeSelecao
              rotulo="Calculado sobre"
              value={base}
              onChange={(e) => setBase(e.target.value as Base)}
              opcoes={[
                { valor: 'FACE', rotulo: `A face (R$ ${reais(n.valorFace)})` },
                { valor: 'VENDA', rotulo: `O valor de venda (R$ ${reais(n.valorVenda)})` },
              ]}
            />
          </div>
          {calculado !== null && (
            <p className="text-sm text-conteudo-secundario">
              = <strong className="text-conteudo-primario">{calculado}</strong>
            </p>
          )}
          <Campo
            rotulo="Quando e como é paga (opcional)"
            placeholder="em até 5 dias após o recebimento"
            value={forma}
            onChange={(e) => setForma(e.target.value)}
          />
        </fieldset>

        <Aviso tom="informacao">
          O termo é gerado e fica guardado na negociação. Para assinar, use "Enviar para assinatura"
          no termo gerado — ele vai ao D4Sign para quem assina pelo fornecedor e pelo comissionado.
        </Aviso>
        {gerar.isError && <ErroDaApi titulo="Não foi possível gerar" erro={gerar.error} />}
      </div>
    </Modal>
  );
}

/** Duas opções lado a lado, como botões de rádio. */
function Alternativas<T extends string>(props: {
  readonly valor: T;
  readonly aoMudar: (valor: T) => void;
  readonly opcoes: readonly { readonly valor: T; readonly rotulo: string }[];
}): JSX.Element {
  return (
    <div role="radiogroup" className="inline-flex w-fit rounded-card border border-borda p-0.5">
      {props.opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          role="radio"
          aria-checked={props.valor === o.valor}
          onClick={() => props.aoMudar(o.valor)}
          className={`rounded-card px-3 py-1.5 text-sm transition-colors ${
            props.valor === o.valor
              ? 'bg-superficie-elevada font-medium text-conteudo-primario shadow-sm'
              : 'text-conteudo-secundario hover:text-conteudo-primario'
          }`}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

function reais(valor: number): string {
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
